<template>
  <div class="generator-new-page" v-if="model">
    <div class="container">
      <!-- 面包屑 -->
      <div class="gen-breadcrumb">
        <router-link to="/">{{ $t('nav.home') }}</router-link>
        <span class="sep">/</span>
        <router-link to="/">{{ $t('footer.aiImages') }}</router-link>
        <span class="sep">/</span>
        <span>{{ model.name }}</span>
      </div>

      <!-- 两栏布局 -->
      <div class="gen-layout-new">
        <!-- 左栏：参数 -->
        <div class="gen-form-col">
          <!-- 可滚动区域 -->
          <div class="gen-form-scroll">
            <!-- 模型选择器 -->
            <div class="gen-model-selector" @click="showModelPicker = !showModelPicker">
              <div class="model-icon">{{ model.name.charAt(0) }}</div>
              <div class="model-info">
                <div class="model-name">{{ model.name }}</div>
                <div class="model-credits">{{ model.credits }} credits &middot; {{ model.speed }}</div>
              </div>
              <svg class="model-arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>

              <!-- 模型下拉 -->
              <div class="gen-model-picker" v-if="showModelPicker" @click.stop>
                <div class="gen-model-picker-search">
                  <input type="text" v-model="modelSearch" :placeholder="$t('home.searchModel')" />
                </div>
                <div class="gen-model-picker-list">
                  <button
                    v-for="m in filteredModels"
                    :key="m.id"
                    :class="['gen-model-picker-item', { active: mixinModelId === m.id }]"
                    @click="switchModel(m.id)"
                  >
                    <span>{{ m.name }}</span>
                    <span class="item-credits">{{ m.credits }} credits</span>
                  </button>
                </div>
              </div>
            </div>

            <!-- Prompt 输入 -->
            <div class="gen-prompt-card">
              <textarea
                class="gen-prompt-textarea"
                v-model="paramValues.prompt"
                :placeholder="$t('home.generatorNew.promptPlaceholderImage')"
                rows="4"
                :maxlength="promptMaxlength"
                @keydown.enter.ctrl="generate"
                @keydown.enter.meta="generate"
              ></textarea>
              <div class="gen-prompt-footer">
                <span class="gen-prompt-count">{{ (paramValues.prompt || '').length }}/{{ promptMaxlength }}</span>
              </div>
            </div>

            <!-- 参数面板 -->
            <div class="gen-params-card">
              <ModelParams
                :modelId="mixinModelId"
                :compact="false"
                :values="paramValues"
                :uploadLoading="uploadLoadingMap"
                :uploadFileNames="uploadFileNames"
                @change="onParamChange"
                @upload="onParamUpload"
                @remove-upload="onRemoveUpload"
              />
            </div>
          </div>

          <!-- 积分 + 生成按钮（固定底部） -->
          <div class="gen-actions gen-actions-fixed">
            <div class="gen-credits-info">
              <span class="credits-num">{{ userCredits }}</span> {{ $t('modelDetail.creditsRequired') }}
              <span :class="insufficientCredits ? 'credits-insufficient' : 'credits-required'">{{ requiredCredits }}</span>
            </div>
            <button
              v-if="!insufficientCredits"
              class="gen-btn-generate"
              @click="generate"
              :disabled="generating"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
              <span>{{ generating ? ($t('home.generatorNew.generating') || 'Generating...') : $t('home.generate') }}</span>
            </button>
            <router-link
              v-else
              class="gen-btn-credits"
              :to="pricingPath"
            >{{ $t('modelDetail.getMoreCredits') }}</router-link>
          </div>

          <!-- 错误消息 -->
          <div class="gen-error-msg" v-if="generateError">{{ generateError }}</div>
        </div>

        <!-- 右栏：历史记录 -->
        <div class="gen-history-col">
          <div class="gen-history-header">
            <span>{{ $t('home.generatorNew.historyTitle') }}</span>
            <router-link class="gen-history-assets-btn" :to="assetsPath">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>
              <span>{{ $t('home.generatorNew.myAssets') }}</span>
            </router-link>
          </div>
          <GenerationHistory
            :items="historyItems"
            type="image"
            :generating="generating"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
var generationMixin = require('../mixins/generationMixin')
var generationHistoryMixin = require('../mixins/generationHistoryMixin')
var ModelParams = require('../components/common/ModelParams.vue').default
var GenerationHistory = require('../components/common/GenerationHistory.vue').default
var getModelParams = require('../config/modelParams').getModelParams

module.exports = {
  name: 'ImageGeneratorNew',
  mixins: [generationMixin, generationHistoryMixin],
  components: {
    ModelParams: ModelParams,
    GenerationHistory: GenerationHistory
  },
  data: function () {
    return {
      mixinType: 'image',
      mixinDefaultAiName: 'Image Model',
      showModelPicker: false,
      modelSearch: ''
    }
  },
  computed: {
    mixinModelId: function () {
      var modelId = this.$route.params.modelId || ''
      /* 别名映射：版本号 URL 复用对应模型的配置 */
      if (modelId === 'seedance-1-5') return 'seedance'
      if (modelId === 'stable-diffusion-3-5') return 'sd-3-5'
      return modelId
    },
    model: function () {
      return this.$store.getters.getModelById(this.mixinModelId)
    },
    relatedModels: function () {
      return this.$store.state.imageModels || []
    },
    filteredModels: function () {
      var s = this.modelSearch.toLowerCase()
      if (!s) return this.relatedModels
      return this.relatedModels.filter(function (m) {
        return m.name.toLowerCase().indexOf(s) > -1 || m.id.toLowerCase().indexOf(s) > -1
      })
    },
    promptMaxlength: function () {
      var config = getModelParams(this.mixinModelId)
      return (config && config.promptMaxlength) || 2000
    },
    pricingPath: function () {
      var lang = this.$route.params.lang
      return lang ? '/' + lang + '/pricing' : '/pricing'
    },
    assetsPath: function () {
      var lang = this.$route.params.lang
      return lang ? '/' + lang + '/gallery' : '/gallery'
    }
  },
  watch: {
    mixinModelId: function () {
      this._fetchHistory()
      this.showModelPicker = false
      this.modelSearch = ''
    }
  },
  mounted: function () {
    var self = this
    this._fetchHistory()
    document.addEventListener('click', self.closePicker)
  },
  beforeDestroy: function () {
    document.removeEventListener('click', this.closePicker)
  },
  methods: {
    switchModel: function (modelId) {
      this.showModelPicker = false
      this.modelSearch = ''
      var lang = this.$route.params.lang
      /* 使用 routeId（版本号 URL 别名） */
      var routeId = modelId
      var m = this.$store.getters.getModelById(modelId)
      if (m && m.routeId) routeId = m.routeId
      var path = '/models/' + routeId
      if (lang) path = '/' + lang + path
      this.$router.push(path)
    },
    closePicker: function (e) {
      if (!e.target.closest('.gen-model-selector') && !e.target.closest('.gen-model-picker')) {
        this.showModelPicker = false
      }
    }
  }
}
</script>

<style scoped>
@import './GeneratorNewStyles.css';
</style>

<style>
/* 左栏白色主题覆盖全局深色样式 */
.generator-new-page .gen-form-col {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 20px;
  overflow: hidden !important;
  overflow-y: hidden !important;
}
/* 可滚动区域 */
.generator-new-page .gen-form-scroll {
  flex: 1 !important;
  min-height: 0 !important;
  overflow-y: auto !important;
  display: flex !important;
  flex-direction: column !important;
  gap: 16px !important;
}
.generator-new-page .gen-actions {
  border-top: none;
}
.generator-new-page .gen-actions-fixed {
  flex-shrink: 0 !important;
  border-top: 1px solid #e5e7eb;
  margin-top: 12px !important;
  background: #fff;
  position: sticky;
  bottom: 0;
  z-index: 1;
}
/* 参数卡片白色主题 — unscoped 以穿透子组件 */
.gen-params-card .param-item { background: none !important; border: none !important; box-shadow: none !important; border-radius: 0 !important; }
.gen-params-card .btn-group { flex-wrap: wrap; column-gap: 10px; row-gap: 10px; }
.gen-params-card .btn-toggle { padding: 8px 14px; font-size: 13px; }
.gen-params-card .option-group label { color: #333; }
.gen-params-card .switch-title { color: #333; }
.gen-params-card .switch-row { color: #555; }
.gen-params-card .form-group label { color: #333; }
.gen-params-card .optional-label { color: #999; }
.gen-params-card .btn-toggle {
  background: #f7f7f8; border-color: #e0e1e5; color: #555;
}
.gen-params-card .btn-toggle.btn-toggle--cols-3 {
  flex: 0 0 calc((100% - 20px) / 3) !important;
  max-width: calc((100% - 20px) / 3) !important;
  min-width: 0 !important;
  width: calc((100% - 20px) / 3) !important;
}
.gen-params-card .btn-toggle:hover { border-color: #059669; }
.gen-params-card .btn-toggle.active {
  background: #059669; border-color: #059669; color: #fff;
}
.gen-params-card .prompt-input {
  background: #f7f7f8; border-color: #e0e1e5; color: #333;
}
.gen-params-card .prompt-input:focus { border-color: #059669; }
.gen-params-card .seed-input {
  background: #f7f7f8; border: 1px solid #e0e1e5; color: #333; border-radius: 8px; padding: 10px 14px; outline: none;
}
.gen-params-card .seed-input:focus { border-color: #059669; }
.gen-params-card .upload-zone {
  background: #f7f7f8; border-color: #ddd; color: #666;
  border-width: 1.5px; padding: 20px; border-radius: 12px; min-height: 120px;
}
.gen-params-card .upload-zone:hover { border-color: #059669; background: rgba(5, 150, 105, 0.04); }
.gen-params-card .upload-zone .upload-hint { color: #999; }
.gen-params-card .upload-zone .upload-preview { max-height: 160px; border-radius: 8px; }
.gen-params-card .upload-preview-sm { max-width: 140px; max-height: 100px; border-radius: 8px; }
.gen-params-card .upload-zone .upload-preview-label { color: #555; }
.gen-params-card .remove-image-btn { background: rgba(0, 0, 0, 0.5); width: 22px; height: 22px; font-size: 12px; }
.gen-params-card .remove-image-btn:hover { background: rgba(255, 0, 0, 0.6); }
.gen-params-card .switch-slider { background: #d1d5db; }
.gen-params-card input:checked + .switch-slider { background: #059669; }
.gen-params-card .size-btn { background: #f7f7f8; border-color: #e0e1e5; }
.gen-params-card .size-btn:hover { border-color: #059669; }
.gen-params-card .size-btn.active { border-color: #059669; background: #059669; }
.gen-params-card .size-label { color: #666; }
.gen-params-card .size-btn.active .size-label { color: #fff; }
.gen-params-card .gen-type-card { background: #f7f7f8; border-color: #e0e1e5; }
.gen-params-card .gen-type-card:hover { border-color: #059669; }
.gen-params-card .gen-type-card.active { border-color: #059669; background: #059669; }
.gen-params-card .gen-type-card-name { color: #333; }
.gen-params-card .gen-type-card.active .gen-type-card-name { color: #fff; }
.gen-params-card .resolution-chip { background: #f7f7f8; border-color: #e0e1e5; color: #555; }
.gen-params-card .resolution-chip:hover { border-color: #059669; }
.gen-params-card .resolution-chip.active { border-color: #059669; background: #059669; color: #fff; }
.gen-params-card .loading-spinner { border-color: #e0e1e5; border-top-color: #059669; }
</style>
